import type { ComponentChildren } from 'preact';

import { cx, Icon } from '@amumin/ui-core';

import { InfoButton, type InfoButtonProps } from './InfoButton.js';
import styles from './PageHeader.module.css';

export interface PageHeaderProps {
  /** De paginatitel (h1): één woord of korte naam, geen datum of groet. */
  title: ComponentChildren;
  /** Terugpijl vóór de titel, alleen op een detailpagina (naar de ouderpagina). */
  back?: { href: string; label: string; name?: string } | undefined;
  /** Zelfde pijl als knop (geen adres), bv. terug naar de lijst in een gesprek op de telefoon. */
  backButton?: { onPress: () => void; label: string; name?: string } | undefined;
  /** Uitleg achter een (i)-knop naast de titel (opent een blad). Niet bij toestemmingen. */
  info?: Omit<InfoButtonProps, 'name'> | undefined;
  /** Rechts: hooguit twee pictogramknoppen (tandwiel, zoeken, ...). */
  tools?: ComponentChildren | undefined;
  /** Rechts, als laatste: de ene primaire actie van de pagina. */
  primary?: ComponentChildren | undefined;
  /** Hooguit één regel `small` onder de kop; lange uitleg hoort in `info`. */
  subtitle?: ComponentChildren | undefined;
  /** Korte regel `meta` onder de kop (bv. de groet op Vandaag). Nooit boven de titel. */
  meta?: ComponentChildren | undefined;
  name?: string | undefined;
}

/**
 * Paginakop van de ouderapp (docs/ux/ontwerpsysteem.md): één rij van 56 px met terugpijl (alleen
 * op een detailpagina), titel, (i)-knop, en rechts de pictogramknoppen en één primaire knop. De
 * titel staat zo op elke pagina op dezelfde hoogte; er staat nooit iets boven.
 */
export function PageHeader({
  title,
  back,
  backButton,
  info,
  tools,
  primary,
  subtitle,
  meta,
  name,
}: PageHeaderProps) {
  return (
    <header class={cx('page-head', styles.head)} data-name={name}>
      <div class={styles.row}>
        {back ? (
          <a
            class={styles.back}
            href={back.href}
            aria-label={back.label}
            title={back.label}
            data-name={back.name ?? 'page-back'}
          >
            <Icon id="arrow-left" size={22} />
          </a>
        ) : backButton ? (
          <button
            type="button"
            class={styles.back}
            aria-label={backButton.label}
            title={backButton.label}
            data-name={backButton.name ?? 'page-back'}
            onClick={backButton.onPress}
          >
            <Icon id="arrow-left" size={22} />
          </button>
        ) : null}
        <h1 class={cx('page-title', styles.title)}>{title}</h1>
        {info ? <InfoButton {...info} name="page-info" /> : null}
        {tools || primary ? (
          <div class={cx('page-actions', styles.actions)}>
            {tools}
            {primary}
          </div>
        ) : null}
      </div>
      {meta ? <p class={cx('page-meta', styles.meta)}>{meta}</p> : null}
      {subtitle ? <p class={cx('page-subtitle', styles.subtitle)}>{subtitle}</p> : null}
    </header>
  );
}

export interface PageBarProps {
  children: ComponentChildren;
  /** Op de telefoon mee plakken onder de bovenbalk (standaard aan). */
  sticky?: boolean | undefined;
  name?: string | undefined;
}

/**
 * Direct onder de paginakop: tabs of een segment (hooguit twee rijen bediening boven de inhoud).
 * Op de telefoon plakt de balk onder de bovenbalk (--b-sticky-top, de app zet die op de hoogte van
 * de bovenbalk).
 */
export function PageBar({ children, sticky = true, name }: PageBarProps) {
  return (
    <div class={cx(styles.bar, sticky && styles.sticky)} data-name={name}>
      {children}
    </div>
  );
}
